import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/capacitor';
import * as SentryReact from '@sentry/react';

// Initialize Sentry in production if DSN is provided
export function initSentry() {
  if (!import.meta.env.PROD) return;

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  // @sentry/capacitor wraps @sentry/react's init to also report native-layer
  // crashes (WebView/plugin crashes the JS SDK alone can't see) on top of
  // everything the web SDK already captures.
  Sentry.init({
    dsn,
    tracePropagationTargets: ['https:geostratapp.com'],
    integrations: [
      SentryReact.browserTracingIntegration(),
      SentryReact.browserProfilingIntegration(),
      SentryReact.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
      // Disable XHR wrapping — Phaser uses XHR to load assets and Sentry's
      // wrapper changes callback timing, causing intermittent black textures.
      SentryReact.browserApiErrorsIntegration({ XMLHttpRequest: false }),
    ],
    tracesSampleRate: 0.1,
    enableLogs: true,
    // Capacitor exposes every registered plugin on window.Capacitor.Plugins
    // as a Proxy that turns *any* property access into a callable plugin
    // method. On web, when something outside our code (browser extensions,
    // crawlers) string-coerces those globals, the Proxy's
    // Symbol.toPrimitive "method" fires and rejects with one of these —
    // unhandled, since nobody awaited it. Our own native calls are all
    // guarded by Capacitor.isNativePlatform(), so on web these are noise.
    ignoreErrors: [/plugin is not implemented on web/],
    beforeSend(event) {
      const ex = event.exception?.values?.[0];
      if (
        Capacitor.getPlatform() === 'web' &&
        ex?.value === 'Cannot convert a Symbol value to a string' &&
        ex.mechanism?.type?.includes('onunhandledrejection')
      ) {
        return null;
      }
      return event;
    },
    // @sentry/capacitor's CapacitorOptions type omits this (it's still a
    // valid option — @sentry/react's init, passed below, is what actually
    // reads it), so the object needs a small type escape hatch.
    ...({
      profilesSampleRate: 1.0,
    } as Record<string, number>),
  }, SentryReact.init);
}

